import { Outlet, type RouteObject } from 'react-router';
import { RedirectIfAuthenticated, RequireAuth } from '@/auth';
import { RouteError } from '@/components/RouteError';
import { AppLayout } from '@/layouts/AppLayout';
import { legalRoutes } from '@/legal/routes';
import { LegalShell } from '@/legal/LegalShell';
import { AuthLayout } from '@/layouts/AuthLayout';
import { MODULES } from '@/modules/registry';
import { AccountPage } from '@/pages/AccountPage';
import { AuthCompletePage } from '@/pages/auth/AuthCompletePage';
import { LoginPage } from '@/pages/auth/LoginPage';
import { HomePage } from '@/pages/HomePage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { RequireAccess } from '@/session/RequireAccess';
import { SessionGate } from '@/session/SessionGate';

/** Every module's routes, each behind the module gate (UX; the API enforces too). */
function moduleRoutes(): RouteObject[] {
  return MODULES.map((module) => ({
    element: (
      <RequireAccess rule={{ module: module.key }}>
        <Outlet />
      </RequireAccess>
    ),
    children: module.routes,
  }));
}

function tenantRoutes(): RouteObject[] {
  return [
    { index: true, element: <HomePage /> },
    ...moduleRoutes(),
    {
      path: '/modulos',
      lazy: async () => ({
        Component: (await import('@/module-picker/ModulesPage')).ModulesPage,
      }),
    },
    {
      path: '/plus',
      lazy: async () => ({ Component: (await import('@/plus/PlusPage')).PlusPage }),
    },
    {
      path: '/indique',
      lazy: async () => ({
        Component: (await import('@/referrals/ReferralsPage')).ReferralsPage,
      }),
    },
    {
      path: '/configuracoes/*',
      lazy: async () => ({
        Component: (await import('@/tenant-admin/TenantSettingsPage')).TenantSettingsPage,
      }),
    },
  ];
}

function platformRoutes(): RouteObject[] {
  const page = (loader: () => Promise<{ Component: React.ComponentType }>) => ({ lazy: loader });
  return [
    {
      index: true,
      ...page(async () => ({
        Component: (await import('@/platform/AdminHomePage')).AdminHomePage,
      })),
    },
    {
      path: '/tenants',
      ...page(async () => ({
        Component: (await import('@/platform/tenants/TenantsPage')).TenantsPage,
      })),
    },
    {
      path: '/tenants/:tenantId/*',
      ...page(async () => ({
        Component: (await import('@/platform/tenants/TenantDetailPage')).TenantDetailPage,
      })),
    },
    {
      path: '/planos',
      ...page(async () => ({ Component: (await import('@/platform/plans/PlansPage')).PlansPage })),
    },
    {
      path: '/vitalicios',
      ...page(async () => ({ Component: (await import('@/platform/LifetimePage')).LifetimePage })),
    },
    {
      path: '/indicacoes',
      ...page(async () => ({
        Component: (await import('@/platform/ReferralsPayoutsPage')).ReferralsPayoutsPage,
      })),
    },
    {
      path: '/interessados',
      ...page(async () => ({ Component: (await import('@/platform/LeadsPage')).LeadsPage })),
    },
    {
      path: '/visitas',
      ...page(async () => ({
        Component: (await import('@/platform/visits/VisitsPage')).VisitsPage,
      })),
    },
    {
      path: '/usuarios',
      ...page(async () => ({ Component: (await import('@/platform/UsersPage')).UsersPage })),
    },
    {
      path: '/consumo',
      ...page(async () => ({ Component: (await import('@/platform/UsagePage')).UsagePage })),
    },
    {
      path: '/ia',
      ...page(async () => ({ Component: (await import('@/platform/ai/AiPage')).AiPage })),
    },
    {
      path: '/auditoria',
      ...page(async () => ({
        Component: (await import('@/platform/AuditPage')).PlatformAuditPage,
      })),
    },
  ];
}

/**
 * Public routes live under <AuthLayout>; private ones require a session for
 * THIS host (<RequireAuth> + <SessionGate>). The platform console and tenant
 * spaces have different route trees: the console simply does not exist on a
 * tenant host, and vice versa.
 */
export function buildRoutes(isPlatform: boolean): RouteObject[] {
  const devRoutes: RouteObject[] = import.meta.env.DEV
    ? [
        {
          path: '/componentes',
          lazy: async () => ({
            Component: (await import('@/pages/ComponentsPage')).ComponentsPage,
          }),
        },
      ]
    : [];
  return [
    {
      errorElement: <RouteError />,
      children: [
        {
          element: (
            <RedirectIfAuthenticated>
              <AuthLayout />
            </RedirectIfAuthenticated>
          ),
          children: [{ path: '/login', element: <LoginPage /> }],
        },
        { path: '/auth/complete', element: <AuthCompletePage /> },
        { element: <LegalShell />, children: legalRoutes() },
        {
          path: '/integracoes/google/concluido',
          lazy: async () => ({
            Component: (await import('@/integrations/drive/DriveCompletePage')).DriveCompletePage,
          }),
        },
        {
          element: (
            <RequireAuth>
              <SessionGate>
                <AppLayout />
              </SessionGate>
            </RequireAuth>
          ),
          children: [
            ...(isPlatform ? platformRoutes() : tenantRoutes()),
            { path: '/conta', element: <AccountPage /> },
            {
              path: '/perfis',
              lazy: async () => ({
                Component: (await import('@/personas/PersonasPage')).PersonasPage,
              }),
            },
            ...devRoutes,
          ],
        },
        { path: '*', element: <NotFoundPage /> },
      ],
    },
  ];
}
